import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { ResourcePermissionService } from "services/resource-permission.service";
import { IResourcePermissionResponse } from "models/church.types";

export default function ResourcePermissionList() {
  const [permissions, setPermissions] = useState<IResourcePermissionResponse[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Calcul du nombre total de pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      let result;
      if (query.trim()) {
        result = await ResourcePermissionService.search(
          query,
          page,
          itemsPerPage,
        );
      } else {
        result = await ResourcePermissionService.getAll(page, itemsPerPage);
      }

      setPermissions(result.data || []);
      setTotalItems(result.totalCount || 0);
    } catch (error) {
      toast.error("Impossible de charger les droits d'accès");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [],
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Voulez-vous vraiment révoquer ces droits d'accès ? L'utilisateur concerné perdra immédiatement ses privilèges sur cette ressource.",
      )
    ) {
      try {
        await ResourcePermissionService.delete(id);
        toast.success("Droits révoqués avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error("Erreur lors de la révocation des droits.");
      }
    }
  };

  // Helper pour afficher élégamment les badges d'autorisation d'action
  const renderPermissionBadge = (allowed: boolean, label: string) => {
    return allowed ? (
      <span className="bg-emerald-950/80 text-emerald-400 border border-emerald-800 text-[10px] font-black px-2 py-0.5 rounded uppercase mr-1">
        {label}
      </span>
    ) : (
      <span className="bg-slate-900/50 text-slate-600 border border-slate-800 text-[10px] font-bold px-2 py-0.5 rounded uppercase mr-1 select-none">
        {label}
      </span>
    );
  };

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* En-tête */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Droits d'Accès aux Ressources
        </h2>
        <p className="text-slate-400 text-sm italic">
          Gestion des permissions d'accès et d'approbation sur les ressources
          de l'application
        </p>
      </div>

      {/* Barre d'outils (Recherche & Ajout) */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-96 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400 group-focus-within:text-[#D4AF37]">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher par utilisateur ou ressource..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>
        <Link
          to="/admin/resource-permissions/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-user-lock mr-2"></i> Octroyer un droit
        </Link>
      </div>

      {/* Tableau des données */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl mb-10">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black border-b border-slate-700">
                <th className="px-6 py-5">ID</th>
                <th className="px-6 py-5">Utilisateur</th>
                <th className="px-6 py-5">Type de Ressource</th>
                <th className="px-6 py-5 text-center">Nom de la Ressource</th>
                <th className="px-6 py-5 text-center">Droits accordés</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {permissions.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-box-open block text-3xl mb-3 opacity-20"></i>
                    Aucun droit d'accès configuré.
                  </td>
                </tr>
              ) : (
                permissions.map((perm) => (
                  <tr
                    key={perm.id}
                    className="hover:bg-slate-700/40 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white font-bold">
                      #{perm.id}
                    </td>
                    <td className="px-6 py-4 text-xs text-white font-bold">
                      <i className="fas fa-user mr-1.5 text-[#D4AF37] opacity-60"></i>
                      {perm.userLogin}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-300 font-medium">
                      <span className="bg-slate-700/50 text-slate-200 border border-slate-600 text-[10px] px-2 py-1 rounded font-mono uppercase font-black">
                        {perm.resourceType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-xs text-slate-400 font-bold">
                      #{perm.resourceId}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center items-center gap-0.5">
                        {renderPermissionBadge(perm.canRead, "Lire")}
                        {renderPermissionBadge(perm.canWrite, "Écrire")}
                        {renderPermissionBadge(perm.canDelete, "Suppr")}
                        {renderPermissionBadge(perm.canApprove, "Appr")}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-4">
                        <Link
                          to={`/admin/resource-permissions/edit/${perm.id}`}
                          className="text-slate-400 hover:text-[#D4AF37]"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(perm.id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Section Pagination */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total global : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            règles d'accès
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
